<template>
  <tr>
    <th>
      <div style="display: flex; align-items: center; gap: 20px;">
        <span style="font-weight: bold;">
          {{ channel.name.value }}
        </span>
        <SensorModelDropdown
          :channel
          :sensorModels
          :sensorModelConfigurationMode
        ></SensorModelDropdown>
      </div>
      <div v-if="controls.length > 0" style="margin-top: 10px; font-size: smaller; font-family: consolas;">
        <template v-for="control in controls">
          {{ controlLabel(control)}}
          <span v-if="showAssignmentWarning(control)" style="color: red;">
            {{ assignmentWarning(control) }}
          </span>
          
          <br>
        </template>
      </div>
    </th>
    <th v-if="ioMode == 3">
      <Checkbox binary :disabled="manualPending" :modelValue="channel.manualSelected.value" @click="toggleChannelManual()"></Checkbox>
    </th>
    <td style="width: 50%;">
      <div class="align-center">
        <template v-if="showSlider">
          <InputText
            :class="{ pending: channel.sensorValue.value != sliderValue }"
            :modelValue="convertedSliderValueString"
            @click="onInputTextClicked"
            @change="onInputTextChanged"
          ></InputText>
          <span class="unitSymbol" v-html="convertedUnitHtml"></span>
          <input v-if="showSlider" type="range" step="0.1" 
            :min="sliderMin"
            :max="sliderMax"
            :value="sliderValue == null ? sliderMin : sliderValue" 
            @input="onSliderInput"
          >
        </template>
        <template v-else>
          <div v-html="valueString"></div>
        </template>
      </div>
    </td>
    <td v-if="canCalibrate">
      <Button severity="secondary" size="small" @click="$emit('calibrate')">
        Calibrate {{ channel.name.value }}
      </Button>
    </td>
  </tr>
</template>

<script>
import store from "./store.js";
import { convertValue, convertUnit, convertedUnitHtml, unconvertValue, unitRound } from "./units.js";
import { getControls, controlLabel } from "./appUtility.js";
import SensorModelDropdown from "./ChannelConfigurationAniSensorModelDropdown.vue"

export default {
  components: { SensorModelDropdown },
  props: [
    "ioMode",
    "port",
    "portNumber",
    "channel",
    "channelNumber",
    "channelMap",
    "sensorModelConfigurationMode"
  ],
  data() {
    return {
      manualPending: false,
      modelPending: false,
      valuePending: false,
      showTextInput: false,
      sliderValue: this.channel.manualCommError.value ? null : this.channel.sensorValue.value
    }
  },
  watch: {
    showSlider() {
      if (!this.showSlider)
        this.showTextInput = false;
    },
    "channel.sensorValue.value": {
      handler(value) {
        if (!this.valuePending && !this.channel.manualCommError.value) {
          this.sliderValue = value;
        }
      }
    },
    "channel.manualCommError.value": {
      handler(value) {
        if (this.channel.manualCommError.value == true)
         this.sliderValue = null;
      }
    }
  },
  computed: {
    session() {
      return store.sessions[0];
    },
    system() {
      return this.session.system;
    },
    sensorModelNumber() {
      let _sensorModelNumber = this.channel.sensorModelNumber.value;
      if (_sensorModelNumber < this.sensorModels.length)
        return _sensorModelNumber;
      else
        return null;
    },
    sensorModels() {
      let session = store.sessions[0];
      return session.system.controller.sensorModels.children;
    },
    sensorModel() {
      let index = this.channel.sensorModelNumber.value;
      if (index < this.sensorModels.length)
        return this.sensorModels[index];
      else
        return null;
    },
    controls() {
      return getControls(this.channelMap, 'ani', 'localhost', this.portNumber, this.channelNumber);
    },
    convertedUnitHtml() {
      let sensorType = store.sessions[0].system.sensorTypes.value[this.sensorModel.sensorType.value];
      return convertedUnitHtml(sensorType.unit);
    },
    isInputManual() {
      return (
        this.ioMode == 1 || 
        this.ioMode == 2 ||
        (this.ioMode == 3 && this.channel.manualSelected.value)
      );
    },
    showSlider() {
      return this.isInputManual && Boolean(this.sensorModel);
    },
    rangeMin() { 
      return this.sensorModel.sensorModelRange.value[0];
    },
    rangeMax() {
      return this.sensorModel.sensorModelRange.value[1];
    },
    sliderTolerance() {
      // A larger tolerance is used for the slider so that range errors can be tested by placing the slider out of range.
      return (this.rangeMax - this.rangeMin) * 0.1;
    },
    sliderMin() {
      return this.rangeMin - this.sliderTolerance;
    },
    sliderMax() {
      return this.rangeMax + this.sliderTolerance;
    },
    convertedSliderValueString() {
      if (this.sliderValue == null) return "";
      let sensorType = store.sessions[0].system.sensorTypes.value[this.sensorModel.sensorType.value];
      let convertedValue = convertValue(this.sliderValue, sensorType.unit);
      let convertedUnit = convertUnit(sensorType.unit);
      return unitRound(convertedValue, convertedUnit);
    },
    canCalibrate() {
      // return this.port.canCalibrate.value;
      return true;
    },
    isTimedOut() {
      return (
        this.port.timers.connectionTimer.value == 0 ||
        this.port.channelCounts.value[0] <= this.channelNumber
      );
    },
    showValueAuto() {
      return Boolean(this.sensorModel) && !this.isTimedOut;
    },
    showValueManual() {
      return Boolean(this.sensorModel);
    },
    valueString() {
      if (this.isTimedOut && !this.isInputManual)
        return "Timed out";
      else if (!this.sensorModel)
        return "No sensor model";
      else {
        let sensorType = this.system.sensorTypes.value[this.sensorModel.sensorType.value];
        return `${this.convertedSliderValueString} ${convertedUnitHtml(sensorType.unit)}`;
      }
    }
  },
  methods: {
    controlLabel,
    toggleChannelManual() {
      this.manualPending = true;
      store.sessions[0].changeValues([{
        handle: this.channel.manualSelected.handle,
        value: !this.channel.manualSelected.value
      }], () => this.manualPending = false);
    },
    showAssignmentWarning(control) {
      return control.sensorType != this.sensorModel.sensorType.value;
    },
    assignmentWarning(control) {
      let sensorType = this.system.sensorTypes.value[control.sensorType];
      return `Requires ${sensorType.name}`;
    },    
    onSliderInput(event) {
      this.setValue(parseFloat(event.target.value));
    },
    onInputTextChanged(event) {
      let sensorType = this.system.sensorTypes.value[this.sensorModel.sensorType.value];
      let convertedValue = parseFloat(event.target.value) || null;
      let convertedUnit = convertUnit(sensorType.unit);
      let unconvertedValue = (convertedValue == null) ? null : unconvertValue(convertedValue, convertedUnit);
      this.setValue(unconvertedValue);
    },    
    setValue(value) 
    {
      value = clamp(value, this.sliderMin, this.sliderMax);
      this.sliderValue = value;
      
      let session = store.sessions[0];
      if (this.valuePending) this.valuePending.cancel();

      this.valuePending = session.queueRequest("/cmd/setAnalogInput", {
        portNumber: this.portNumber,
        channelNumber: this.channelNumber,
        value: this.sliderValue
      }, () => { 
        this.valuePending = false; 
      });
    },
    onInputTextClicked(event) {
      event.target.select();
    }
  }
}

function clamp(value, min, max) {
  if (value == null) return null;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
</script>

<style scoped>
th, td {
  padding: 10px;
  vertical-align: top;
}
input.p-inputtext {
  width: 75px;
  text-align: center;
}
input.p-inputtext.pending {
  outline: none;
  border: 2px solid rgb(20, 94, 179);
}
.unitSymbol {
  font-weight: 300;
}
input[type="range"] {
  width: 90%;
  flex-grow: 1;
}
.align-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding-left: 10px;
  padding-right: 10px;
}
.value-string {
  padding: 0;
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
}
.value-string.pending {
  font-weight: bold;
  font-style: italic;
}
button {
  white-space: nowrap;
}
</style>
